import { useEffect, useRef, useState } from "react";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridColumnMenuFilter,
  GridColumnMenuProps,
  GridDataStateChangeEvent,
  GridExpandChangeEvent,
  GridNoRecords,
} from "@progress/kendo-react-grid";

import {
  setExpandedState,
  setGroupIds,
} from "@progress/kendo-react-data-tools";
import { DataResult, process, State } from "@progress/kendo-data-query";
import { useGetPaymentGateway } from "../../apiclient/master/useGetPaymentGatewayApi";
import PaymentGatewayModel from "../../models/masters/paymentgateway-model";
import { SubmitStatus } from "../../core/formik-model-dialog.component";
import { API_END_POINT } from "../../constants";
import { GridCommandCell } from "../../core/grid-command-cell.component";
import { LoadingIndicator } from "../../core/loading-indicator.component";
import {
  DeleteModelDialog,
  DeletePostDataInfo,
  DeleteStatus,
} from "../../core/delete-model-dialog.component";
import PaymentGatewayEntry from "./paymentgateway-entry";
import { GridActionComponent } from "../../core/action.title.component";

export const addOrUpdateArrayItem = (data: any[], item: any, key: string) => {
  const newData = data ? [...data] : [];
  const index = data?.findIndex((d) => d[key] === item[key]);
  console.log("the index is" + index);
  if (index > -1) {
    newData.splice(index, 1, item);
  } else {
    newData.push(item);
  }
  return newData;
};

const PaymentGatewayPage: React.FC = () => {
  const initialDataState: State = {
    take: 10,
    skip: 0,
    group: [],
  };
  const [dataState, setDataState] = useState<State>(initialDataState);
  const [deletePaymentGatewayData, setDeletePaymentGatewayData] = useState<DeletePostDataInfo>();
  const [collapsedState, setCollapsedState] = useState<string[]>([]);
  const [resultData, setResultData] = useState<DataResult>();

  const [editModal, setEditModal] = useState<boolean>(false);
  const [editData, setEditdata] = useState<PaymentGatewayModel>(null);
  const exportExcel = useRef<ExcelExport | null>(null);

  const [pgreset, setpgreset] = useState<boolean>(false);
  const [isViewMode, setViewMode] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    data: result,
    isLoading,
    isError,
    refetch: reloadPaymentGateway,
  } = useGetPaymentGateway();

  const processWithGroups = (data: PaymentGatewayModel[], dataState: State) => {
    const groups = dataState.group;
    dataState.group = groups;
    const newDataState = process(data, dataState);
    setGroupIds({ data: newDataState.data, group: dataState.group });
    return newDataState;
  };

  const fetchData = async () => {
    try {
      setIsRefreshing(true);
      const { data, error } = await reloadPaymentGateway();
      if (error) {
        console.error("Error reloading:", error);
      } else {
        console.log("Reloaded data:", data);
      }
    } catch (err) {
      console.error("Unexpected error:", err);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    setEditdata(null);
    fetchData();
    if (result) {
      setResultData(processWithGroups(result, initialDataState));
    }
  }, [result]);

  let newData: any = null;
  if (resultData) {
    newData = setExpandedState({
      data: resultData.data,
      collapsedIds: collapsedState,
    });
  }

  const onSaveComplete = (status: SubmitStatus, data: PaymentGatewayModel) => {
    if (status === SubmitStatus.Completed) {
      reloadPaymentGateway();
      setResultData(processWithGroups(newData, dataState));
    }
    setEditdata(null);
  };

  const excelExport = () => {
    if (exportExcel.current !== null) {
      exportExcel.current.save();
    }
  };

  const onAddNew = () => {
    setEditModal(true);
    setViewMode(false);
    setEditdata(new PaymentGatewayModel());
  };

  const onEdit = (data: PaymentGatewayModel) => {
    setViewMode(false);
    setEditModal(true);
    setEditdata(data);
  };

  const onView = (data: PaymentGatewayModel) => {
    setViewMode(true);
    setEditdata(data);
  };

  const onRemove = (data: PaymentGatewayModel) => {
    const { paymentGatewayId } = data;
    if (paymentGatewayId > 0) {
      setDeletePaymentGatewayData({
        data,
        route: `PaymentGateway/DeletePaymentGateway/${paymentGatewayId}`,
        endpoint: API_END_POINT.MASTER,
      });
      reloadPaymentGateway();
    }
  };

  const onDeleteComplete = (status: DeleteStatus, data: PaymentGatewayModel) => {
    if (status === DeleteStatus.Completed) {
      reloadPaymentGateway();
      setResultData(processWithGroups(result, dataState));
    }
    setDeletePaymentGatewayData(null);
  };

  const ColumnMenu = (props: GridColumnMenuProps) => (
    <div>
      <GridColumnMenuFilter {...props} expanded={true} />
    </div>
  );

  const CommandCell = (props: GridCellProps) => (
    <GridCommandCell
      {...props}
      edit={onEdit}
      remove={onRemove}
      add={onAddNew}
      view={onView}
      dataItem={props.dataItem}
      idPropertyName="paymentGatewayId"
      editPermissionName={null}
      deletePermissionName={null}
    />
  );

  const onSearch = (e: any) => {
    if (e.target.value.length > 0) {
      const searchKey: string = e.target.value;
      const filteredData = result?.filter(
        (p) =>
          p?.paymentGatewayName?.toLowerCase().includes(searchKey?.toLowerCase())
      );
      setResultData(process(filteredData, dataState));
    } else {
      setResultData(process(result, dataState));
    }
  };

  const expandChange = (event: GridExpandChangeEvent) => {
    const item = event.dataItem;
    if (item.groupId) {
      const newCollapsedIds = !event.value
        ? [...collapsedState, item.groupId]
        : collapsedState.filter((groupId) => groupId !== item.groupId);
      setCollapsedState(newCollapsedIds);
    }
  };

  const ColumnFormatBoolean = (props: any) => (
    <td
      {...props.tdProps}
      colSpan={1}
      style={{
        color: "black",
      }}
    >
      {props.children === "true" ? (
        <span style={{ color: "green", fontWeight: "bold" }}>Yes</span>
      ) : (
        <span style={{ color: "red", fontWeight: "bold" }}>No</span>
      )}
    </td>
  );

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    const newDataState = processWithGroups(result, event.dataState);
    setResultData(newDataState);
    setDataState(event.dataState);
  };

  const onAddClick = () => {
    setEditModal(true);
    setViewMode(false);
    setEditdata(new PaymentGatewayModel());
  };

  let grid = null;
  if (result) {
    grid = (
      <Grid
        data={newData}
        pageable={{ pageSizes: [10, 20, 50, 100] }}
        total={resultData?.total}
        groupable={true}
        sortable={true}
        onDataStateChange={dataStateChange}
        {...dataState}
        onExpandChange={expandChange}
        columnVirtualization={false}
        resizable={true}
      >
        <GridColumn
          field="paymentGatewayName"
          title="Payment Gateway Name"
          columnMenu={ColumnMenu}
        />
        <GridColumn
          field="merchantId"
          title="Merchant Id"
          columnMenu={ColumnMenu}
        />
          <GridColumn
          field="typeId"
          title="Type"
        />
        <GridColumn
          field="gatewayApiKey"
          title="Gateway Api Key"
        />
        <GridColumn
          field="gatewaySecretKey"
          title="Gateway Secret Key"
        />
        <GridColumn
          cell={CommandCell}
          title="Action"
          groupable={true}
        />
        <GridNoRecords>
          <div className="mt-2" style={{ width: "100%", height: "500px" }}>
            <center>
              <h5>No data found</h5>
            </center>
          </div>
        </GridNoRecords>
      </Grid>
    );
  }

  if (isError) {
    return (
      <div>
        <h4>Error Loading Page..</h4>
      </div>
    );
  }

  return (
    <>
      <div
        style={{ minHeight: "600px" }}
        className="card p-3 border-0 m-3 sw-shadow radius-15"
      >
        <div>
          <GridActionComponent
            onSearchClick={onSearch}
            addButtonClick={onAddClick}
            excelExportClick={excelExport}
            refreshClick={fetchData}
            title="PAYMENT GATEWAY"
            addButtonTooltip="Add Payment Gateway"
          />
        </div>

        {editModal && (
          <PaymentGatewayEntry
            isViewMode={isViewMode}
            data={editData}
            onClose={onSaveComplete}
          />
        )}
        {isViewMode && (
          <PaymentGatewayEntry
            isViewMode={true}
            data={editData}
            onClose={onSaveComplete}
          />
        )}
        {deletePaymentGatewayData && (
          <DeleteModelDialog
            title="Delete PaymentGateway"
            deleteData={deletePaymentGatewayData}
            onClose={onDeleteComplete}
          >
            <div>
              Are you sure you want to delete Payment Gateway:{" "}
              <span style={{ fontWeight: "bold" }}>
                {deletePaymentGatewayData.data.paymentGatewayName}
              </span>
              ?
            </div>
          </DeleteModelDialog>
        )}

        <div>
          <>
            {isRefreshing || isLoading ? (
              <LoadingIndicator isLoading={isRefreshing} />
            ) : (
              <div>
                {grid}
                <ExcelExport
                  fileName={"PaymentGateway_List"}
                  data={result}
                  ref={exportExcel}
                >
                  <ExcelExportColumn width={100} field="name" title="Name" />
                  <ExcelExportColumn
                    width={100}
                    field="districtName"
                    title="District"
                  />
                </ExcelExport>
              </div>
            )}
          </>
        </div>
      </div>
    </>
  );
};

export default PaymentGatewayPage;
